import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { Ticket, Plus, Trash2, Users, Car, UserCheck, Zap, Edit2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromotionsPage = () => {
  const { token } = useAdmin();
  const [promoCodes, setPromoCodes] = useState([]);
  const [autoRules, setAutoRules] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);

  // Form state
  const [newCode, setNewCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(10);
  const [target, setTarget] = useState('both');
  const [description, setDescription] = useState('');
  const [maxUses, setMaxUses] = useState(0);
  const [expiresAt, setExpiresAt] = useState('');

  // Auto-rule form state
  const [autoTarget, setAutoTarget] = useState('riders');
  const [autoRideNumber, setAutoRideNumber] = useState(1);
  const [autoDiscount, setAutoDiscount] = useState(10);
  const [autoDescription, setAutoDescription] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/promotions/codes`, { headers }),
        fetch(`${API_URL}/api/admin/promotions/auto-rules`, { headers })
      ]);
      const codesData = await codesRes.json();
      const rulesData = await rulesRes.json();
      if (codesData.success) setPromoCodes(codesData.promo_codes || []);
      if (rulesData.success) setAutoRules(rulesData.rules || []);
    } catch (e) {
      console.error('Failed to fetch promo data:', e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      const body = {
        code: newCode || null,
        discount_percent: discountPercent,
        target,
        description,
        max_uses: maxUses,
        expires_at: expiresAt || null
      };
      const res = await fetch(`${API_URL}/api/admin/promotions/codes`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setPromoCodes([data.promo_code, ...promoCodes]);
        resetForm();
        const count = data.notifications_sent || 0;
        if (count > 0) {
          alert(`Promo code created! ${count} notification${count > 1 ? 's' : ''} sent to eligible users.`);
        }
      } else {
        alert(data.detail || 'Failed to create code');
      }
    } catch { alert('Network error'); }
  };

  const handleUpdateCode = async (codeId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/promotions/codes/${codeId}`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
        setEditingCode(null);
      }
    } catch { alert('Network error'); }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/promotions/codes/${codeId}`, {
        method: 'DELETE', headers
      });
      const data = await res.json();
      if (data.success) {
        setPromoCodes(promoCodes.filter(p => p.id !== codeId));
      }
    } catch { alert('Network error'); }
  };

  const handleCreateAutoRule = async (e) => {
    e.preventDefault();
    try {
      const body = {
        target: autoTarget,
        ride_number: autoRideNumber,
        discount_percent: autoDiscount,
        enabled: true,
        description: autoDescription
      };
      const res = await fetch(`${API_URL}/api/admin/promotions/auto-rules`, {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        fetchData();
        setShowAutoForm(false);
        setAutoDescription('');
      } else {
        alert(data.detail || 'Failed to create rule');
      }
    } catch { alert('Network error'); }
  };

  const handleDeleteAutoRule = async (ruleId) => {
    if (!window.confirm('Delete this auto-promo rule?')) return;
    try {
      await fetch(`${API_URL}/api/admin/promotions/auto-rules/${ruleId}`, {
        method: 'DELETE', headers
      });
      setAutoRules(autoRules.filter(r => r.id !== ruleId));
    } catch { alert('Network error'); }
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setNewCode('');
    setDiscountPercent(10);
    setTarget('both');
    setDescription('');
    setMaxUses(0);
    setExpiresAt('');
  };

  const getTargetBadge = (t) => {
    const styles = {
      riders: 'bg-blue-100 text-blue-800',
      drivers: 'bg-amber-100 text-amber-800',
      both: 'bg-green-100 text-green-800'
    };
    const icons = { riders: Users, drivers: Car, both: UserCheck };
    const Icon = icons[t] || UserCheck;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${styles[t] || styles.both}`}>
        <Icon className="w-3 h-3" />
        {t === 'both' ? 'All Users' : t.charAt(0).toUpperCase() + t.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-promotions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
          <p className="text-gray-600 mt-1">Manage promo codes and automatic discounts</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          data-testid="create-promo-btn"
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Promo Code
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Promo Code</h3>
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code (leave blank to auto-generate)</label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME50"
                  data-testid="promo-code-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(Number(e.target.value))}
                  data-testid="discount-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="target-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                >
                  <option value="both">Both (Riders & Drivers)</option>
                  <option value="riders">Riders Only</option>
                  <option value="drivers">Drivers Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (0 = unlimited)</label>
                <input
                  type="number"
                  min="0"
                  value={maxUses}
                  onChange={(e) => setMaxUses(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Welcome discount for new riders"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At (optional)</label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="submit-promo-btn" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                Create Code
              </button>
              <button type="button" onClick={resetForm} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Promo Codes Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-green-600" />
            Promo Codes ({promoCodes.length})
          </h3>
        </div>
        {promoCodes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No promo codes yet. Create one to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Uses</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {promoCodes.map((promo) => (
                  <tr key={promo.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded">{promo.code}</span>
                      {promo.description && <p className="text-xs text-gray-500 mt-1">{promo.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-lg font-bold text-green-600">{promo.discount_percent}%</span>
                    </td>
                    <td className="px-4 py-3">{getTargetBadge(promo.target)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {promo.used_count || 0}{promo.max_uses > 0 ? ` / ${promo.max_uses}` : ' / ∞'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleUpdateCode(promo.id, { active: !promo.active })}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          promo.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {promo.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDeleteCode(promo.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auto Promo Rules */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Automatic New User Discounts
          </h3>
          <button
            onClick={() => setShowAutoForm(!showAutoForm)}
            data-testid="add-auto-rule-btn"
            className="flex items-center gap-1 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1.5 rounded-lg font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Rule
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Set automatic discounts for new users on their first, second, or third ride/trip. These apply automatically without needing a promo code.
          </p>

          {showAutoForm && (
            <form onSubmit={handleCreateAutoRule} className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">For</label>
                  <select
                    value={autoTarget}
                    onChange={(e) => setAutoTarget(e.target.value)}
                    data-testid="auto-target-select"
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="riders">Riders</option>
                    <option value="drivers">Drivers</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Ride #</label>
                  <select
                    value={autoRideNumber}
                    onChange={(e) => setAutoRideNumber(Number(e.target.value))}
                    data-testid="auto-ride-number-select"
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value={1}>1st Ride</option>
                    <option value={2}>2nd Ride</option>
                    <option value={3}>3rd Ride</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={autoDiscount}
                    onChange={(e) => setAutoDiscount(Number(e.target.value))}
                    data-testid="auto-discount-input"
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={autoDescription}
                    onChange={(e) => setAutoDescription(e.target.value)}
                    placeholder="Welcome discount"
                    className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" data-testid="save-auto-rule-btn" className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
                  Save Rule
                </button>
                <button type="button" onClick={() => setShowAutoForm(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1.5 rounded-lg text-sm font-medium">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {autoRules.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              No auto-promo rules set. New users won't get automatic discounts.
            </div>
          ) : (
            <div className="space-y-3">
              {autoRules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-sm">#{rule.ride_number}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {rule.discount_percent}% off {rule.ride_number === 1 ? '1st' : rule.ride_number === 2 ? '2nd' : '3rd'} ride
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {getTargetBadge(rule.target)}
                        {rule.description && <span className="text-xs text-gray-500">{rule.description}</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteAutoRule(rule.id)}
                    className="text-red-500 hover:text-red-700 p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionsPage;
